import { motion, AnimatePresence } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { PHOTOS } from "@/lib/photos";
import lotMapBase from "@/assets/lot-map-roostertail.png";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";
import {
  Car,
  Bus,
  Ship,
  MapPin,
  Check,
  ExternalLink,
  Smartphone,
  Music,
  Footprints,
} from "lucide-react";

// ── Locations ───────────────────────────────────────────────
// Swap the embed `q=` targets if Teddy supplies exact share links.
const ROOSTERTAIL_ADDRESS = "100 Marquette Dr, Detroit, MI 48214";
const RIVERSIDE_ADDRESS = "Riverside Marina, 11000 Freud St, Detroit, MI 48214";
const ROOSTERTAIL_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  ROOSTERTAIL_ADDRESS
)}`;
const RIVERSIDE_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  RIVERSIDE_ADDRESS
)}`;
// Keyless Google Maps embed endpoint (the target maps.google.com?output=embed
// redirects to) — returns 200 with no x-frame-options.
const embedUrl = (query: string) =>
  `https://www.google.com/maps/embed?origin=mfe&pb=!1m2!2m1!1s${encodeURIComponent(
    query
  )}`;
const roostertailEmbed = embedUrl(ROOSTERTAIL_ADDRESS);
const riversideEmbed = embedUrl(RIVERSIDE_ADDRESS);

// OnTourHQ reserved-parking checkout (separate event from festival tickets)
const PARKING_CHECKOUT_URL = "https://events.ontourhq.com/e/yrd-parking/tickets";

const noiseBg = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
};

// ── Reserved lots at the Roostertail ────────────────────────
const RESERVED_LOTS = [
  {
    key: "premium",
    name: "Premium Parking",
    subLabel: "MAIN LOT · CLOSEST TO THE ENTRANCE",
    price: "$100 / day",
    blurb:
      "Premium reserved parking in the Roostertail's main lot, closest to the festival entrance. Only 20 spots per day.",
    features: [
      "Steps from the festival entrance",
      "Reserved spot guaranteed all day — arrive whenever you like",
      "Only 20 spots available per day",
    ],
  },
  {
    key: "medium",
    name: "Medium Lot",
    subLabel: "SHORT WALK TO THE ENTRANCE",
    price: "$40 / day",
    blurb:
      "Reserved parking in the Roostertail's medium lot — a short walk to the festival entrance.",
    features: [
      "Short, easy walk to the gates",
      "Reserved spot guaranteed all day — arrive whenever you like",
      "50 spots available per day",
    ],
  },
  {
    key: "back",
    name: "Back Lot",
    subLabel: "BEST VALUE ON PROPERTY",
    price: "$30 / day",
    blurb:
      "Reserved parking in the Roostertail's back lot — the lowest-priced reserved option, a few minutes' walk to the festival entrance.",
    features: [
      "Lowest-priced reserved parking on the property",
      "Reserved spot guaranteed all day — arrive whenever you like",
      "30 spots available per day",
    ],
  },
];

// ── Quick FAQ ───────────────────────────────────────────────
const PARKING_FAQ = [
  {
    q: "How much is parking?",
    a: "General admission parking at Riverside Marina is $20 per vehicle, paid at the lot when you arrive — cash only, no reservation needed. Check in at the Yacht Rock Detroit tent when you pull in; bathrooms are on site. Reserved parking on the Roostertail property starts at $30 per day.",
  },
  {
    q: "How do I get from Riverside Marina to the festival?",
    a: "Two ways, both free with your ticket: a 5–6 minute shuttle ride straight to the festival doors (two shuttles running constantly, both days), or an 8-minute ferry ride down the Detroit River right to the High Noon deck.",
  },
  {
    q: "Are the shuttle and ferry really free?",
    a: "Yes — both are included with your festival ticket. No extra charge, no reservation. They run continuously both days.",
  },
  {
    q: "Can I leave and come back to my parking spot?",
    a: "No in-and-out privileges at any lot. You can arrive whenever you like, but once you're parked, you're parked for the day.",
  },
  {
    q: "Is reserved parking good for both days?",
    a: "Reserved parking is sold per day. If you're coming Friday and Saturday, you'll need a parking pass for each day.",
  },
  {
    q: "Should I just take an Uber?",
    a: "Honestly? We recommend it. Rideshare drop-off is right at the Roostertail entrance (100 Marquette Dr) — no parking, no shuttle, no waiting after the show ends.",
  },
];

const FaqRow = ({
  q,
  a,
  isOpen,
  onToggle,
}: {
  q: string;
  a: string;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <div className="border-b border-yacht-charcoal/10 last:border-b-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 py-5 text-left cursor-pointer"
      aria-expanded={isOpen}
    >
      <span className="text-yacht-charcoal font-bold text-base md:text-lg leading-snug">
        {q}
      </span>
      <motion.span
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.2 }}
        className="text-2xl font-light leading-none text-yacht-blue flex-shrink-0"
      >
        +
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="overflow-hidden"
        >
          <p className="pb-5 text-yacht-charcoal/65 text-sm md:text-base leading-relaxed max-w-2xl">
            {a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/**
 * Visual aid: how close each lot is to the festival entrance.
 * Horizontal walk-line on desktop, vertical on mobile.
 */
const LOT_DISTANCES = [
  { name: "Premium (Main Lot)", walk: "Steps away", price: "$100", emphasis: true },
  { name: "Medium Lot", walk: "Short walk", price: "$40", emphasis: false },
  { name: "Back Lot", walk: "A few minutes", price: "$30", emphasis: false },
];

const WalkDistanceVisual = () => (
  <motion.div
    className="rounded-2xl bg-yacht-charcoal p-6 md:p-8 mb-8 md:mb-10"
    style={{
      boxShadow:
        "0 12px 32px -16px rgba(0,0,0,0.35), 0 4px 12px -6px rgba(0,0,0,0.18)",
    }}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5 }}
  >
    <p className="text-white/55 text-[11px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-5 md:mb-6 text-center">
      How close is each lot?
    </p>

    {/* Desktop: horizontal walk line */}
    <div className="hidden md:flex items-start">
      <div className="flex flex-col items-center flex-shrink-0 w-36">
        <div className="w-12 h-12 rounded-full bg-yacht-yellow flex items-center justify-center mb-2">
          <Music className="w-5 h-5 text-yacht-charcoal" />
        </div>
        <span className="text-white font-bold text-sm text-center leading-tight">
          Festival
          <br />
          Entrance
        </span>
      </div>
      {LOT_DISTANCES.map((lot) => (
        <div key={lot.name} className="flex items-start flex-1 min-w-0">
          <div className="flex-1 flex flex-col items-center pt-5 px-1">
            <div className="w-full border-t-2 border-dashed border-white/25" />
            <span className="text-white/50 text-[11px] mt-1.5 whitespace-nowrap flex items-center gap-1">
              <Footprints className="w-3 h-3" /> {lot.walk}
            </span>
          </div>
          <div className="flex flex-col items-center flex-shrink-0 w-32">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                lot.emphasis
                  ? "bg-yacht-yellow"
                  : "bg-white/10 border border-white/25"
              }`}
            >
              <Car
                className={`w-5 h-5 ${
                  lot.emphasis ? "text-yacht-charcoal" : "text-white/80"
                }`}
              />
            </div>
            <span className="text-white font-bold text-sm text-center leading-tight">
              {lot.name}
            </span>
            <span
              className={`text-xs font-bold mt-1 ${
                lot.emphasis ? "text-yacht-yellow" : "text-white/55"
              }`}
            >
              {lot.price}/day
            </span>
          </div>
        </div>
      ))}
    </div>

    {/* Mobile: vertical walk line */}
    <div className="md:hidden flex flex-col">
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-full bg-yacht-yellow flex items-center justify-center flex-shrink-0">
          <Music className="w-5 h-5 text-yacht-charcoal" />
        </div>
        <span className="text-white font-bold text-sm">Festival Entrance</span>
      </div>
      {LOT_DISTANCES.map((lot) => (
        <div key={lot.name}>
          <div className="flex items-center gap-2 py-1 ml-[21px]">
            <div className="h-8 border-l-2 border-dashed border-white/25" />
            <span className="text-white/50 text-[11px] flex items-center gap-1">
              <Footprints className="w-3 h-3" /> {lot.walk}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div
              className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
                lot.emphasis
                  ? "bg-yacht-yellow"
                  : "bg-white/10 border border-white/25"
              }`}
            >
              <Car
                className={`w-5 h-5 ${
                  lot.emphasis ? "text-yacht-charcoal" : "text-white/80"
                }`}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold text-sm">{lot.name}</span>
              <span
                className={`text-xs font-bold ${
                  lot.emphasis ? "text-yacht-yellow" : "text-white/55"
                }`}
              >
                {lot.price}/day
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

/**
 * Labeled lot map — OSM base image with branded pins for each lot.
 * Pin positions are percentages of the image (1000x1120 crop, verified
 * against Teddy's marked-up screenshot: bottom strip = Premium,
 * Sindbad's rows = Medium, Freud St lot = Back).
 */
const LOT_MAP_PINS = [
  { label: "Back Lot", x: 30.0, y: 13.9, accent: false },
  { label: "Medium Lot", x: 47.0, y: 28.6, accent: false },
  { label: "Premium — Main Lot", x: 40.0, y: 55.4, accent: true },
  { label: "The Roostertail", x: 50.5, y: 79.0, accent: false, venue: true },
];

const LotMap = () => (
  <motion.div
    className="bg-white rounded-2xl overflow-hidden shadow-lg border border-yacht-charcoal/10"
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5 }}
  >
    <div className="relative">
      <img
        src={lotMapBase}
        alt="Map of the Roostertail parking lots: Back Lot near Freud Street, Medium Lot by Sindbad's, and the Premium Main Lot on Marquette Drive closest to the venue"
        className="w-full h-auto block"
        loading="lazy"
        decoding="async"
      />
      {LOT_MAP_PINS.map((pin) => (
        <div
          key={pin.label}
          className="absolute -translate-x-1/2 -translate-y-full flex flex-col items-center"
          style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
        >
          <span
            className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] md:text-xs font-bold shadow-md ${
              pin.venue
                ? "bg-yacht-blue text-white"
                : pin.accent
                  ? "bg-yacht-yellow text-yacht-charcoal"
                  : "bg-yacht-charcoal text-white"
            }`}
          >
            {pin.label}
          </span>
          <span
            className={`w-0 h-0 border-l-[6px] border-r-[6px] border-t-[7px] border-l-transparent border-r-transparent ${
              pin.venue
                ? "border-t-yacht-blue"
                : pin.accent
                  ? "border-t-yacht-yellow"
                  : "border-t-yacht-charcoal"
            }`}
          />
        </div>
      ))}
      <span className="absolute bottom-1 right-2 text-[9px] text-yacht-charcoal/50 bg-white/70 px-1 rounded">
        Map © OpenStreetMap contributors
      </span>
    </div>
    <div className="px-5 md:px-6 py-4 flex items-start gap-3">
      <MapPin className="w-5 h-5 text-yacht-blue flex-shrink-0 mt-0.5" />
      <p className="text-yacht-charcoal/70 text-sm leading-relaxed">
        All three lots are on the Roostertail property along Marquette Drive —
        enter from E Jefferson Ave at Marquette Dr and staff will direct you to
        your lot.
      </p>
    </div>
  </motion.div>
);

const LotAccordionRow = ({
  lot,
  isOpen,
  onToggle,
  onReserve,
  delay = 0,
}: {
  lot: (typeof RESERVED_LOTS)[number];
  isOpen: boolean;
  onToggle: () => void;
  onReserve: () => void;
  delay?: number;
}) => (
  <motion.div
    className="relative overflow-hidden rounded-2xl bg-yacht-charcoal border border-yacht-charcoal/15"
    style={{
      boxShadow:
        "0 12px 32px -16px rgba(0,0,0,0.35), 0 4px 12px -6px rgba(0,0,0,0.18)",
    }}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5, delay }}
  >
    <span
      className="absolute left-0 top-0 bottom-0 w-1 md:w-1.5 bg-yacht-yellow"
      aria-hidden
    />
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 py-6 md:py-7 pl-6 md:pl-10 pr-5 md:pr-8 transition-colors text-left hover:bg-white/5 cursor-pointer"
      aria-expanded={isOpen}
    >
      <span className="flex flex-col gap-1 md:flex-row md:items-baseline md:gap-5 min-w-0">
        <span className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight uppercase leading-tight text-white">
          {lot.name}
        </span>
        <span className="text-[10px] sm:text-[11px] md:text-sm font-semibold tracking-wider whitespace-nowrap text-white/55">
          {lot.subLabel}
        </span>
      </span>
      <span className="flex items-center gap-4 flex-shrink-0">
        <span className="text-yacht-yellow font-bold text-sm md:text-lg whitespace-nowrap">
          {lot.price}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-3xl md:text-4xl font-light leading-none w-6 md:w-7 text-center"
          style={{ color: "#F7B41C" }}
        >
          +
        </motion.span>
      </span>
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="overflow-hidden"
        >
          <div className="px-6 md:px-10 pb-8 md:pb-10 max-w-2xl">
            <p className="text-white/65 text-sm md:text-base leading-relaxed mb-5">
              {lot.blurb}
            </p>
            <ul className="space-y-3 mb-6">
              {lot.features.map((text, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-yacht-yellow/20 flex items-center justify-center">
                    <Check
                      className="w-2.5 h-2.5 text-yacht-yellow"
                      strokeWidth={3}
                    />
                  </span>
                  <span className="text-sm md:text-base text-white/85 leading-relaxed">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-white/45 text-xs md:text-sm leading-relaxed mb-6">
              Sold per day — Friday (July 17) and Saturday (July 18) passes are
              separate. No in-and-out privileges: arrive whenever you like, but
              once you're parked, you're parked for the day.
            </p>
            <button
              onClick={onReserve}
              className="w-full md:w-auto md:px-14 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base md:text-lg hover:brightness-110 transition-all cursor-pointer"
              style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
            >
              Reserve a Spot →
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const MapCard = ({
  title,
  address,
  embedSrc,
  mapsUrl,
}: {
  title: string;
  address: string;
  embedSrc: string;
  mapsUrl: string;
}) => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-yacht-charcoal/10">
    <div className="aspect-[4/3] w-full">
      <iframe
        src={embedSrc}
        title={`Map to ${title}`}
        className="w-full h-full border-0"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>
    <div className="p-5 md:p-6">
      <div className="flex items-start gap-3 mb-3">
        <MapPin className="w-5 h-5 text-yacht-blue flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-yacht-charcoal font-bold text-lg leading-tight">
            {title}
          </h3>
          <p className="text-yacht-charcoal/60 text-sm mt-1">{address}</p>
        </div>
      </div>
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-yacht-blue font-semibold text-sm hover:underline"
      >
        Open in Google Maps <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  </div>
);

const Parking = () => {
  usePageSEO({
    title: "Parking — Yacht Rock Detroit 2026 | Roostertail & Riverside Marina",
    description:
      "Parking for Yacht Rock Detroit 2026 at The Roostertail, July 17–18. $20 general admission parking at Riverside Marina with free shuttle & ferry, or reserved lots on the Roostertail property.",
    keywords:
      "yacht rock detroit parking, roostertail parking, riverside marina parking detroit, detroit festival parking, roostertail shuttle, detroit river ferry festival",
    ogImage: PHOTOS.CROWD.sunsetStageView,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Parking", url: "https://yachtrockdetroit.com/parking" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  const [openLot, setOpenLot] = useState<string | null>("premium");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // Load OnTourHQ modal script pointed at the parking event
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://events.ontourhq.com/ts_modal.js";
    script.async = true;
    script.onload = () => {
      (window as any).TSModals?.buildModal({
        url: PARKING_CHECKOUT_URL,
        modalTriggerElementId: "modal-trigger-element-id",
      });
    };
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleReserve = () => {
    (window as any).fbq?.("track", "ViewContent", {
      content_name: "Yacht Rock Detroit 2026 Parking",
      content_category: "Parking",
    });
    document.getElementById("modal-trigger-element-id")?.click();
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* Hidden OnTourHQ modal trigger */}
      <button
        id="modal-trigger-element-id"
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: "fixed",
          left: "-9999px",
          width: "1px",
          height: "1px",
          opacity: 0.01,
        }}
      />

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative bg-yacht-blue overflow-hidden pt-28 md:pt-40 pb-14 md:pb-20"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={noiseBg}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-8 text-center">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={
              isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
          >
            Getting There · July 17–18
          </motion.p>
          <div
            className="text-[10vw] md:text-7xl font-bold text-white leading-[0.95] tracking-tight"
            role="heading"
            aria-level={1}
          >
            <GradualSpacing text="Parking" duration={0.5} delayMultiple={0.04} />
          </div>
          <motion.div
            className="mt-8 max-w-xl mx-auto text-left space-y-3"
            initial={{ opacity: 0, y: 20 }}
            animate={
              isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <a
              href="#general-admission"
              className="flex items-start gap-4 rounded-xl bg-white/10 border border-white/15 px-5 py-4 hover:bg-white/15 transition-colors"
            >
              <span className="text-yacht-yellow font-bold text-lg md:text-xl leading-none mt-0.5">
                1
              </span>
              <span className="text-white text-sm md:text-base leading-snug">
                <strong>General Admission — Riverside Marina.</strong>{" "}
                <span className="text-white/75">
                  $20 cash at the lot, free shuttle &amp; ferry to the doors.
                  Where most people park.
                </span>
              </span>
            </a>
            <a
              href="#reserved-parking"
              className="flex items-start gap-4 rounded-xl bg-white/10 border border-white/15 px-5 py-4 hover:bg-white/15 transition-colors"
            >
              <span className="text-yacht-yellow font-bold text-lg md:text-xl leading-none mt-0.5">
                2
              </span>
              <span className="text-white text-sm md:text-base leading-snug">
                <strong>Reserved parking at the Roostertail.</strong>{" "}
                <span className="text-white/75">
                  Guaranteed spots on the property, sold per day. See below ↓
                </span>
              </span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* GENERAL ADMISSION PARKING */}
      <section id="general-admission" className="py-16 md:py-24 px-6 md:px-8 scroll-mt-20">
        <div className="max-w-5xl mx-auto">
          <motion.p
            className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            How Most People Do It
          </motion.p>
          <motion.h2
            className="text-3xl md:text-5xl font-bold text-yacht-charcoal mb-10 md:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            General Admission Parking
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl p-7 md:p-9 shadow-lg border border-yacht-charcoal/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-yacht-blue/10 flex items-center justify-center">
                    <Car className="w-5 h-5 text-yacht-blue" />
                  </div>
                  <div>
                    <h3 className="text-yacht-charcoal font-bold text-xl leading-tight">
                      Riverside Marina
                    </h3>
                    <p className="text-yacht-charcoal/55 text-xs uppercase tracking-wider font-semibold mt-0.5">
                      General Admission Lot
                    </p>
                  </div>
                </div>
                <p className="text-4xl md:text-5xl font-bold text-yacht-charcoal mb-1">
                  $20
                </p>
                <p className="text-yacht-charcoal/55 text-sm mb-5">
                  per vehicle · pay when you arrive · <strong className="text-yacht-charcoal/75">cash only</strong>
                </p>
                <ul className="space-y-2.5">
                  {[
                    "No reservation needed — first come, first served",
                    "Check in at the Yacht Rock Detroit tent when you arrive",
                    "Bathrooms on site",
                    "Free shuttle or ferry to the festival, included with your ticket",
                    "Lots fill as the day goes on — early birds ride first",
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-yacht-blue/10 flex items-center justify-center">
                        <Check
                          className="w-2.5 h-2.5 text-yacht-blue"
                          strokeWidth={3}
                        />
                      </span>
                      <span className="text-sm md:text-base text-yacht-charcoal/75 leading-relaxed">
                        {text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <MapCard
                title="Riverside Marina"
                address="11000 Freud St, Detroit, MI 48214"
                embedSrc={riversideEmbed}
                mapsUrl={RIVERSIDE_MAPS_URL}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FREE SHUTTLE + FERRY */}
      <section className="relative bg-yacht-blue overflow-hidden py-16 md:py-24 px-6 md:px-8">
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={noiseBg}
        />
        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-3 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Free With Your Ticket
          </motion.p>
          <motion.h2
            className="text-3xl md:text-5xl font-bold text-white mb-10 md:mb-14 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Shuttle & Ferry to the Doors
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <motion.div
              className="bg-white/[0.07] border border-white/15 rounded-2xl p-7 md:p-9"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-12 h-12 rounded-full bg-yacht-yellow/15 flex items-center justify-center mb-5">
                <Bus className="w-6 h-6 text-yacht-yellow" />
              </div>
              <h3 className="text-white font-bold text-xl md:text-2xl mb-3">
                The Shuttle
              </h3>
              <p className="text-white/70 text-sm md:text-base leading-relaxed">
                A 5–6 minute ride from Riverside Marina straight to the festival
                doors. Two shuttles run constantly back and forth, both days —
                you'll never wait long.
              </p>
            </motion.div>

            <motion.div
              className="bg-white/[0.07] border border-white/15 rounded-2xl p-7 md:p-9"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="w-12 h-12 rounded-full bg-yacht-yellow/15 flex items-center justify-center mb-5">
                <Ship className="w-6 h-6 text-yacht-yellow" />
              </div>
              <h3 className="text-white font-bold text-xl md:text-2xl mb-3">
                The Ferry
              </h3>
              <p className="text-white/70 text-sm md:text-base leading-relaxed">
                An 8-minute ride down the Detroit River, docking right at the
                High Noon deck. Arriving at a yacht rock festival by boat? That's
                the assignment.
              </p>
            </motion.div>
          </div>

          <motion.p
            className="text-white/50 text-sm md:text-base text-center mt-8 md:mt-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Both are free and included with your festival ticket — running
            continuously, both days.
          </motion.p>
        </div>
      </section>

      {/* RESERVED PARKING */}
      <section id="reserved-parking" className="py-16 md:py-24 px-6 md:px-8 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <motion.p
            className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Park On The Property
          </motion.p>
          <motion.h2
            className="text-3xl md:text-5xl font-bold text-yacht-charcoal mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Reserved Parking at the Roostertail
          </motion.h2>
          <motion.p
            className="text-yacht-charcoal/60 text-base md:text-lg leading-relaxed mb-10 md:mb-12 max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            A limited number of reserved spots right on the Roostertail
            property. Sold per day — grab Friday, Saturday, or both.
          </motion.p>

          <WalkDistanceVisual />

          <div className="space-y-4 md:space-y-5">
            {RESERVED_LOTS.map((lot, i) => (
              <LotAccordionRow
                key={lot.key}
                lot={lot}
                isOpen={openLot === lot.key}
                onToggle={() =>
                  setOpenLot(openLot === lot.key ? null : lot.key)
                }
                onReserve={handleReserve}
                delay={i * 0.08}
              />
            ))}
          </div>

          <div id="lot-map" className="mt-10 md:mt-12 grid md:grid-cols-2 gap-6 md:gap-8 items-start scroll-mt-24">
            <LotMap />
            <MapCard
              title="The Roostertail"
              address={ROOSTERTAIL_ADDRESS}
              embedSrc={roostertailEmbed}
              mapsUrl={ROOSTERTAIL_MAPS_URL}
            />
          </div>

          <p className="text-yacht-charcoal/70 text-base md:text-lg text-center mt-10 md:mt-12">
            Would you like to dock your boat here?{" "}
            <a
              href="/guide#bring-your-boat"
              className="text-yacht-blue font-semibold underline underline-offset-4 hover:text-yacht-blue/80"
            >
              Fill out this form
            </a>
            .
          </p>
        </div>
      </section>

      {/* RIDESHARE */}
      <section className="py-14 md:py-20 px-6 md:px-8 bg-yacht-yellow relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={noiseBg}
        />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-yacht-charcoal/10 flex items-center justify-center mx-auto mb-5">
            <Smartphone className="w-6 h-6 text-yacht-charcoal" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-yacht-charcoal mb-4">
            Or Skip Parking Entirely
          </h2>
          <p
            className="text-yacht-charcoal/70 text-base md:text-xl leading-relaxed"
            style={{ fontFamily: "Georgia, serif" }}
          >
            We recommend Uber or Lyft — drop-off is right at the Roostertail
            entrance at 100 Marquette Dr. No lot, no shuttle, no finding your
            car at midnight.
          </p>
        </div>
      </section>

      {/* QUICK FAQ */}
      <section className="py-16 md:py-24 px-6 md:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.p
            className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Quick Answers
          </motion.p>
          <motion.h2
            className="text-3xl md:text-5xl font-bold text-yacht-charcoal mb-8 md:mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Parking FAQ
          </motion.h2>

          <div className="bg-white rounded-2xl px-6 md:px-9 py-2 shadow-lg border border-yacht-charcoal/10">
            {PARKING_FAQ.map((item, i) => (
              <FaqRow
                key={i}
                q={item.q}
                a={item.a}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>

          <p className="text-yacht-charcoal/50 text-sm text-center mt-8">
            More questions?{" "}
            <a href="/faq" className="text-yacht-blue font-semibold hover:underline">
              See the full FAQ
            </a>{" "}
            or{" "}
            <a
              href="/contact"
              className="text-yacht-blue font-semibold hover:underline"
            >
              get in touch
            </a>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Parking;
